/**
 * planGates — a PURE, read-only TypeScript mirror of the canonical
 * `empowernow_common.mcp.cda.gate_planner.CDAGatePlanner` ordering.
 *
 * This is a PREDICTION of the gates a contract would cause. It is NOT the
 * runtime boundary — the PDP, delegation broker, and signed receipts are the
 * real enforcement, and `planGates` can drift from them. The `CdaGateLadder`
 * component therefore carries an explicit `provenance` ('predicted' | 'enforced')
 * so the UI never presents this prediction as ground truth.
 *
 * Ordering pinned to the Python planner (gate_planner.py, `plan`):
 *   1. apply x-default values            (mechanical)
 *   2. apply x-normalize transformations (mechanical)
 *   3. collect missing required params   (decision; sensitive x-redact = secure)
 *   4. risk-based confirmation / WAITING (decision)
 *   + CDA extensions surfaced for the operator/author:
 *     step-up (required_amr), batch consent (x-confirm-scope), value disclosure.
 *
 * The always-on platform invariants (live PDP authorization, signed receipt) are
 * NOT part of this contract-derived plan; the ladder renders them as fixed
 * framing rows so the prediction stays a faithful mirror of the planner.
 */

import type { CdaContract } from './types';
import {
  parseRiskLevel,
  riskRequiresConfirmation,
  riskRequiresWaiting,
} from './types';

export type GateKind = 'mechanical' | 'decision';

export type GateState =
  /** Happens automatically, no human in the loop. */
  | 'auto'
  /** A human/authority decision is required before proceeding. */
  | 'required'
  /** Satisfied by the simulated args. */
  | 'satisfied'
  /** Not applicable for the simulated args. */
  | 'skipped'
  /** Blocked — cannot proceed under the simulated conditions. */
  | 'blocked'
  /** Suspended pending out-of-band enterprise approval (WAITING). */
  | 'waiting';

/** Which visual channel a step belongs to (governance is not severity). */
export type GateChannel = 'risk' | 'coverage' | 'neutral';

export interface GateStep {
  id: string;
  label: string;
  kind: GateKind;
  state: GateState;
  detail?: string;
  channel: GateChannel;
}

export interface PlanGatesOptions {
  /** Simulated arguments. When omitted, gates render in their declared form. */
  args?: Record<string, unknown>;
  /** Whether the (simulated) client supports MCP form elicitation. */
  supportsElicitation?: boolean;
  /** Whether risk has already been confirmed (simulated). */
  alreadyConfirmed?: boolean;
}

const BUILTIN_NORMALIZERS: ReadonlySet<string> = new Set([
  'lower',
  'lowercase',
  'upper',
  'uppercase',
  'trim',
  'strip',
  'email',
  'normalize_email',
  'lower_trim',
]);

function isValueMissing(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
}

/**
 * Produce the ordered, predicted gate plan for a contract.
 * Deterministic: same contract + options always yield the same steps.
 */
export function planGates(contract: CdaContract, options: PlanGatesOptions = {}): GateStep[] {
  const { args, alreadyConfirmed } = options;
  const simulated = !!args;
  const resolved: Record<string, unknown> = args ? { ...args } : {};

  const steps: GateStep[] = [];

  // --- Step 1: x-default / default (mechanical) -----------------------------
  const defaultable = contract.params.filter(
    (p) => p['x-default'] !== undefined || p.default !== undefined,
  );
  if (defaultable.length > 0) {
    let applied = 0;
    if (simulated) {
      for (const p of defaultable) {
        if (isValueMissing(resolved[p.name])) {
          resolved[p.name] = p['x-default'] !== undefined ? p['x-default'] : p.default;
          applied += 1;
        }
      }
    }
    steps.push({
      id: 'defaults',
      label: 'Apply defaults',
      kind: 'mechanical',
      state: simulated ? (applied > 0 ? 'satisfied' : 'skipped') : 'auto',
      detail: `${defaultable.length} parameter${defaultable.length === 1 ? '' : 's'} carry a default`,
      channel: 'neutral',
    });
  }

  // --- Step 2: x-normalize (mechanical) -------------------------------------
  const normalizable = contract.params.filter(
    (p) => typeof p['x-normalize'] === 'string' && BUILTIN_NORMALIZERS.has(p['x-normalize'] as string),
  );
  if (normalizable.length > 0) {
    steps.push({
      id: 'normalize',
      label: 'Normalize inputs',
      kind: 'mechanical',
      state: simulated ? 'satisfied' : 'auto',
      detail: normalizable.map((p) => p.name).join(', '),
      channel: 'neutral',
    });
  }

  // --- Step 3: collect missing required params (decision) -------------------
  const requiredParams = contract.params.filter((p) => p.required);
  if (requiredParams.length > 0) {
    const missing = simulated
      ? requiredParams.filter((p) => isValueMissing(resolved[p.name]))
      : requiredParams;
    const sensitive = missing.filter((p) => p['x-redact'] === true);

    let state: GateState;
    if (simulated) {
      state = missing.length === 0 ? 'satisfied' : 'required';
    } else {
      state = 'required';
    }

    if (sensitive.length > 0) {
      steps.push({
        id: 'secure-input',
        label: 'Secure input (redacted)',
        kind: 'decision',
        state,
        detail: `${sensitive.map((p) => p.name).join(', ')} collected via secure URL elicitation; value is redacted from logs and model context`,
        channel: 'coverage',
      });
    }

    const regular = missing.filter((p) => p['x-redact'] !== true);
    if (regular.length > 0 || sensitive.length === 0) {
      const shown = simulated ? regular : requiredParams.filter((p) => p['x-redact'] !== true);
      steps.push({
        id: 'inputs',
        label: 'Collect required inputs',
        kind: 'decision',
        state: simulated ? (regular.length === 0 ? 'satisfied' : 'required') : 'required',
        detail: shown.length > 0 ? shown.map((p) => p.name).join(', ') : 'all required inputs present',
        channel: 'neutral',
      });
    }
  }

  // --- Step 4: risk-based confirmation / WAITING (decision) -----------------
  const risk = parseRiskLevel(contract['x-risk']);
  if (riskRequiresWaiting(risk)) {
    steps.push({
      id: 'waiting',
      label: 'Enterprise approval (WAITING)',
      kind: 'decision',
      state: 'waiting',
      detail:
        contract['x-confirm-message'] ||
        'Critical risk — suspends for out-of-band enterprise approval before execution',
      channel: 'risk',
    });
  } else if (riskRequiresConfirmation(risk)) {
    steps.push({
      id: 'confirm',
      label: 'Human confirmation',
      kind: 'decision',
      state: simulated && alreadyConfirmed ? 'satisfied' : 'required',
      detail: contract['x-confirm-message'] || `Confirm before executing (risk: ${risk})`,
      channel: 'coverage',
    });
  } else if (contract['x-cda'] === true) {
    // CDA explicitly requested confirmation even if risk does not gate on its own.
    steps.push({
      id: 'confirm',
      label: 'Human confirmation',
      kind: 'decision',
      state: simulated && alreadyConfirmed ? 'satisfied' : 'required',
      detail: contract['x-confirm-message'] || 'Confirm before executing',
      channel: 'coverage',
    });
  }

  // --- CDA extensions: step-up MFA + batch consent --------------------------
  const scope = contract['x-confirm-scope'];
  if (scope) {
    if (Array.isArray(scope.required_amr) && scope.required_amr.length > 0) {
      steps.push({
        id: 'step-up',
        label: 'Step-up authentication',
        kind: 'decision',
        state: 'required',
        detail: `Requires ${scope.required_amr.join(', ')} at consent mint time`,
        channel: 'coverage',
      });
    }
    if (scope.batchable === true && scope.grant && scope.grant !== 'per-call') {
      const bounded =
        scope.grant === 'per-tool-list'
          ? `bounded list${scope.max_candidates ? ` (≤ ${scope.max_candidates})` : ''}, each item single-use`
          : `standing budget${scope.max_operations ? ` (≤ ${scope.max_operations} ops)` : ''}`;
      steps.push({
        id: 'consent',
        label: 'Batch consent',
        kind: 'decision',
        state: 'required',
        detail: `${scope.grant}: ${bounded}. PDP + delegation still run per operation.`,
        channel: 'coverage',
      });
    }
  }

  // --- Value disclosure (mechanical, platform-enforced) ---------------------
  if (contract['x-discloses-value'] === true) {
    steps.push({
      id: 'disclosure',
      label: 'Secure value reveal',
      kind: 'mechanical',
      state: 'auto',
      detail: 'Plaintext rides a client-bound reveal frame, withheld from the model and ordered ahead of any same-target destroy',
      channel: 'coverage',
    });
  }

  return steps;
}

/** Convenience: the decision gates only, for compact summaries. */
export function decisionGates(steps: GateStep[]): GateStep[] {
  return steps.filter((s) => s.kind === 'decision');
}
